import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Switch, Label, useToast, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input } from '@nimbitlabs/ui';
import { CreditCard, Calendar, TrendingUp, Check, Gift } from 'lucide-react';
import { useSubscriptionStore } from './subscriptionStore';
import { useAccountStore } from '../account/accountStore';
import type { BillingInterval } from './types';

export function SubscriptionPage() {
  const { limits, products = [], activeSubscription, scheduledSubscriptions, loading, fetchProducts, fetchActiveSubscription, fetchScheduledSubscriptions, createSubscription: createSub, invalidateAll } = useSubscriptionStore();
  const { updateReferralCode } = useAccountStore();
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
    fetchActiveSubscription();
    fetchScheduledSubscriptions();
  }, [fetchProducts, fetchActiveSubscription, fetchScheduledSubscriptions]);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [pendingSubscription, setPendingSubscription] = useState<{ productId: string; optionId: string; amount: number } | null>(null);

  // Filter active products
  const activeProducts = products.filter(p => p.isActive);

  const handleSelectPlan = (productId: string, optionId: string, amount: number) => {
    // Store the subscription details
    setPendingSubscription({ productId, optionId, amount });
    
    // Check if this is a downgrade (lower price than current plan)
    const currentAmount = activeSubscription?.amount ? parseFloat(activeSubscription.amount) : 0;
    const isDowngrade = amount < currentAmount;
    
    // Skip referral dialog for downgrades, show it for upgrades/new subscriptions
    if (isDowngrade) {
      handleConfirmSubscription();
    } else {
      setShowReferralDialog(true);
    }
  };

  const handleConfirmSubscription = async () => {
    if (!pendingSubscription) return;

    const { productId, optionId, amount } = pendingSubscription;
    setSelectedPlan(productId);
    setShowReferralDialog(false);

    try {
      // Save referral code if provided
      if (referralCode.trim()) {
        try {
          await updateReferralCode({ referralCode: referralCode.trim() });
        } catch (error) {
          console.error('Failed to save referral code:', error);
          // Continue with subscription even if referral fails
        }
      }

      // Create subscription
      const response = await createSub({
        planCode: optionId,
        amount,
        billingInterval: billingInterval,
        callbackUrl: `${window.location.origin}/billing/return`,
      });

      if (response.authorizationUrl) {
        window.location.href = response.authorizationUrl;
      } else {
        toast({ title: 'Success', description: response.message });
        // Invalidate all subscription queries
        await invalidateAll();
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to process subscription', variant: 'destructive' });
    } finally {
      setSelectedPlan(null);
      setPendingSubscription(null);
      setReferralCode('');
    }
  };

  const handleSkipReferral = () => {
    handleConfirmSubscription();
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <CreditCard className="h-8 w-8" />
          Subscription
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your subscription and billing
        </p>
      </div>

      {/* Pending Plan Change Banner */}
      {scheduledSubscriptions && scheduledSubscriptions.length > 0 && (
        <div className="relative overflow-hidden rounded-lg border border-cyan-400/40 bg-cyan-500/10">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 to-transparent"></div>
          
          <div className="relative px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center rounded-md bg-cyan-400/20 h-8 w-8">
                  <Calendar className="h-4 w-4 text-cyan-400" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                {scheduledSubscriptions.map((sub) => {
                  // Parse the date properly - it comes from the database as ISO string
                  const dateStr = sub.scheduled_start_date || sub.current_period_end;
                  const changeDate = dateStr ? new Date(dateStr) : null;
                  
                  const formattedDate = changeDate && !isNaN(changeDate.getTime())
                    ? changeDate.toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'Date pending';
                  
                  const isDowngrade = sub.change_type === 'downgrade';
                  
                  return (
                    <p key={sub.id} className="text-sm text-foreground">
                      You can continue to enjoy your current plan features until{' '}
                      <span className="font-semibold text-cyan-400">{formattedDate}</span>
                      {isDowngrade && (
                        <span className="text-muted-foreground">
                          {' '}when your plan will change to <span className="font-medium text-foreground">{sub.planName}</span>
                        </span>
                      )}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4 py-4">
        <Label htmlFor="billing-toggle" className={billingInterval === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
          Monthly
        </Label>
        <Switch
          id="billing-toggle"
          checked={billingInterval === 'annually'}
          onCheckedChange={(checked) => setBillingInterval(checked ? 'annually' : 'monthly')}
          className="data-[state=checked]:bg-primary"
        />
        <Label htmlFor="billing-toggle" className={billingInterval === 'annually' ? 'font-semibold' : 'text-muted-foreground'}>
          Annual
        </Label>
      </div>

      {/* Available Plans */}
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Choose Your Plan</h2>
          <p className="text-muted-foreground mt-2">
            Select the plan that best fits your needs
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading plans...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeProducts.map((product, index) => {
              const monthlyOption = product.options?.find(o => o.billingInterval?.toLowerCase() === 'monthly' && o.isActive);
              const annualOption = product.options?.find(o => o.billingInterval?.toLowerCase() === 'annually' && o.isActive);
              const option = billingInterval === 'monthly' ? monthlyOption : annualOption;
              
              // Check current plan using active subscription data (case-insensitive)
              const currentPlanName = (activeSubscription?.planName || limits?.plan_name || 'free').toLowerCase();
              const isCurrentPlan = product.name?.toLowerCase() === currentPlanName;
              const price = option?.price || 0;
              const isTeams = product.name?.toLowerCase() === 'teams';
              
              // Calculate annual savings
              let savingsPercent = 0;
              if (billingInterval === 'annually' && monthlyOption && annualOption) {
                const monthlyYearly = monthlyOption.price * 12;
                const annualPrice = annualOption.price;
                if (monthlyYearly > 0) {
                  savingsPercent = Math.round(((monthlyYearly - annualPrice) / monthlyYearly) * 100);
                }
              }
              
              // Extract feature descriptions from the features object
              const featureList = product.features 
                ? Object.values(product.features).map((f: any) => f.description).filter(Boolean)
                : [];

              return (
                <Card key={product.id} className={`relative flex flex-col mt-6 ${isCurrentPlan ? 'border-primary border-2' : isTeams ? 'border-primary border-2 shadow-lg' : ''}`}>
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-full">
                      Current Plan
                    </div>
                  )}
                  {isTeams && !isCurrentPlan && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-600 text-white text-xs font-bold px-6 py-2 rounded-full shadow-xl whitespace-nowrap">
                        ⭐ MOST POPULAR
                      </span>
                    </div>
                  )}
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl font-bold">{product.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
                    <div className="mt-6 flex items-baseline gap-1">
                      <span className="text-sm text-muted-foreground">R</span>
                      <span className="text-5xl font-bold tracking-tight">{price.toFixed(0)}</span>
                      <span className="text-muted-foreground">/{billingInterval === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    {billingInterval === 'annually' && savingsPercent > 0 && (
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 dark:bg-green-950/20 px-2 py-1 rounded-full">
                          <TrendingUp className="h-3 w-3" />
                          Save {savingsPercent}% annually
                        </span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col pt-0">
                    <ul className="space-y-3 flex-1 mb-6">
                      {featureList.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-full bg-green-100 dark:bg-green-950/30 p-0.5">
                            <Check className="h-4 w-4 text-green-600 dark:text-green-500" />
                          </div>
                          <span className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full mt-auto"
                      variant={isCurrentPlan ? 'outline' : isTeams ? 'default' : 'outline'}
                      size="lg"
                      disabled={isCurrentPlan || selectedPlan === product.id || !option}
                      onClick={() => option && !isCurrentPlan && handleSelectPlan(product.id, option.code || option.id, price)}
                    >
                      {selectedPlan === product.id ? 'Processing...' : isCurrentPlan ? '✓ Subscribed' : 'Select Plan'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Referral Code Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Have a Referral Code?
            </DialogTitle>
            <DialogDescription>
              If you were referred by someone, enter their referral code to give them credit. This is optional and won't affect your subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="referral-code">Referral Code (Optional)</Label>
              <Input
                id="referral-code"
                placeholder="Enter referral code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmSubscription();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleSkipReferral}
              className="w-full sm:w-auto"
            >
              Skip
            </Button>
            <Button
              onClick={handleConfirmSubscription}
              className="w-full sm:w-auto"
            >
              Continue to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
